import { Component, Input, NgZone, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as JSZip from 'jszip';
import { MessageService } from 'primeng/api';
import { filter, firstValueFrom, switchMap } from 'rxjs';
import { EXPORT_OPTIONS, TEMPLATE_EXPORT_TYPES } from 'src-ironside/constants/export-product';
import { ProductExtendInfo } from 'src-ironside/repositories/product.repository';
import { CommonService } from 'src-ironside/services/common.service';
import { PermissionService } from 'src-ironside/services/permission.service';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { downloadAllResource } from 'src-ironside/utils/dowload-file-zip';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { environment } from 'src/environments/environment.ironside';

@Component({
    selector: 'app-export-data-product',
    templateUrl: './export-data-product.component.html',
    styleUrls: ['./export-data-product.component.css'],
})
export class ExportDataProductComponent implements OnInit {
    @Input() disabledBtnImport: boolean = false; /* !products?.length */
    @Input() products: any[] = [];
    @Input() idParentProduct: string = '';
    @Input() idProductSelected: string[] = [];
    @Input() exportOption: any[] = EXPORT_OPTIONS;
    @Input() height: number = 0;

    loadingExport: boolean = false;
    exportVisible: boolean = false;
    itemExportSelected: any[] = [];
    isProgress: boolean = false;
    progressNumber: number = 0;
    interval: any;
    isExportCancelled: boolean = false;
    finalZipBlob: Blob | null = null;

    constructor(
        public sessionStorageService: SessionStorageService,
        private ngZone: NgZone,
        private _messageService: MessageService,
        private translate: TranslateService,
        private _commonService: CommonService,
        private dynamicDocumentService: DynamicDocumentService,
        public permissionService: PermissionService,
    ) {}

    ngOnInit(): void {}

    updateExportPermission(fonctions: string[], ref: string, key: string): void {
        const hasPermission = fonctions?.includes(ref) ?? true;
        this.exportOption.forEach((el) => {
            if (el.key === key) {
                el.permission = hasPermission;
            }
        });
    }

    onShowTypeExport() {
        if (
            (this.products?.[0]?.length ?? 0) > 0 &&
            this.exportOption.some((el) => el?.permission === true)
        ) {
            this.exportVisible = !this.exportVisible;
        }
    }

    isDisabled(key: string): boolean {
        return this.itemExportSelected?.length >= 3 && !this.itemExportSelected.includes(key);
    }

    onCancelExport() {
        this.isExportCancelled = true;
        this.exportVisible = false;
        this.loadingExport = false;
        this.isProgress = false;
        this.progressNumber = 0;
        this.itemExportSelected = [];
        this.finalZipBlob = null;
        clearInterval(this.interval);
    }

    async onExportResource() {
        this.isExportCancelled = false;
        this.loadingExport = true;
        this.isProgress = true;
        this.progressExport();

        // Check if we have any template-based exports
        const templateExports = this.itemExportSelected.filter((type) =>
            TEMPLATE_EXPORT_TYPES.includes(type),
        );
        const photoExports = this.itemExportSelected.filter((type) => type === 'PHOTO');
        const regularExports = this.itemExportSelected.filter(
            (type) => !TEMPLATE_EXPORT_TYPES.includes(type) && type !== 'PHOTO',
        );

        const mappedArrays = regularExports.reduce(
            (acc, key) => {
                acc[key] = [];
                return acc;
            },
            {} as Record<string, any[]>,
        );

        if (regularExports.length > 0) {
            const selectedProducts =
                this.idProductSelected.length > 0
                    ? this.products?.[0]?.filter((product: any) =>
                          this.idProductSelected.includes(product.id),
                      ) || []
                    : this.products?.[0] || [];

            const resource = selectedProducts
                .flatMap((product: ProductExtendInfo) => product.resources || [])
                .filter((item: any) => item?.files?.length);

            regularExports.forEach((item) => {
                mappedArrays[item] = resource?.filter((el: any) => el?.typeOfResource === item);
            });
        }

        const exportPromises = [
            ...templateExports.map((type) =>
                this.handleTemplateExport(type).catch((error) => {
                    const exportOption = this.exportOption.find((el) => el?.key === type);
                    this._messageService.add({
                        severity: 'error',
                        detail: this.translate.instant('export.DOWNLOAD_ERROR', {
                            item: exportOption?.label,
                        }),
                    });
                    throw error;
                }),
            ),
            ...photoExports.map(() =>
                firstValueFrom(
                    this.dynamicDocumentService.exportPhotosByProductId(this.idParentProduct),
                ).catch((error) => {
                    const exportOption = this.exportOption.find((el) => el?.key === 'PHOTO');
                    this._messageService.add({
                        severity: 'error',
                        detail: this.translate.instant('export.DOWNLOAD_ERROR', {
                            item: exportOption?.label,
                        }),
                    });
                    throw error;
                }),
            ),
            ...Object.entries(mappedArrays).map(([_, resources], index) => {
                return downloadAllResource(resources as any).catch((error) => {
                    const exportOption = this.exportOption.find(
                        (el) => el?.key === Object.keys(mappedArrays)[index],
                    );

                    this._messageService.add({
                        severity: 'error',
                        detail: this.translate.instant('export.DOWNLOAD_ERROR', {
                            item: exportOption?.label,
                        }),
                    });
                    throw error;
                });
            }),
        ];

        if (this.itemExportSelected.length === 1 && this.itemExportSelected[0] === 'PHOTO') {
            Promise.allSettled(exportPromises).then((results) => {
                if (this.isExportCancelled) return;

                const result = results[0];
                if (result.status === 'fulfilled' && result.value) {
                    const photoOption = this.exportOption.find((el) => el?.key === 'PHOTO');
                    const label = this.translate.instant('export.' + photoOption?.label);

                    const url = URL.createObjectURL(result.value);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${label}.zip`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }

                this.progressNumber = 100;
                this.loadingExport = false;
                this.isProgress = false;
                clearInterval(this.interval);
            });
            return;
        }

        Promise.allSettled(exportPromises)
            .then((results) => {
                if (this.isExportCancelled) return;

                const finalZip = new JSZip();
                let successCount = 0;
                let errorCount = 0;

                const unzipPromises: Promise<void>[] = [];

                results.forEach((result, index) => {
                    let exportType: string;

                    if (index < templateExports.length) exportType = templateExports[index];
                    else if (index < templateExports.length + photoExports.length)
                        exportType = 'PHOTO';
                    else
                        exportType =
                            Object.keys(mappedArrays)[
                                index - templateExports.length - photoExports.length
                            ];

                    const exportOption = this.exportOption.find((el) => el?.key === exportType);

                    if (result.status === 'fulfilled' && result.value) {
                        if (TEMPLATE_EXPORT_TYPES.includes(exportType)) {
                            finalZip.file(
                                `${this.translate.instant('export.' + exportOption?.label)}.${
                                    exportType.includes('CATALOGUE') ||
                                    exportType.includes('CATALOGUE_NO_PRICE') ||
                                    exportType.includes('CATALOGUE_DATA_SHEET')
                                        ? 'pdf'
                                        : 'xlsx'
                                }`,
                                result.value,
                            );
                        } else if (exportType === 'PHOTO') {
                            unzipPromises.push(
                                JSZip.loadAsync(result.value)
                                    .then(async (innerZip) => {
                                        const entryNames = Object.keys(innerZip.files);
                                        console.log(
                                            '[EXPORT PHOTO] inner zip entries:',
                                            entryNames,
                                        );
                                        const tasks = entryNames
                                            .filter((name) => !innerZip.files[name].dir)
                                            .map(async (entryName) => {
                                                const blob =
                                                    await innerZip.files[entryName].async('blob');
                                                finalZip.file(`photos/${entryName}`, blob);
                                            });
                                        await Promise.all(tasks);
                                    })
                                    .catch((error) => {
                                        console.error(
                                            '[EXPORT PHOTO] Failed to unzip archive : ',
                                            error,
                                        );
                                    }),
                            );
                        } else {
                            finalZip.file(
                                `${this.translate.instant('export.' + exportOption?.label)}.zip`,
                                result.value,
                            );
                        }
                        successCount++;
                    } else {
                        errorCount++;
                    }
                });

                this.progressNumber = 100;
                clearInterval(this.interval);

                if (this.isExportCancelled) return;

                if (successCount === 0) {
                    this.loadingExport = false;
                    this.isProgress = false;
                    this.progressNumber = 0;
                    return;
                }

                return Promise.all(unzipPromises).then(() =>
                    finalZip.generateAsync({ type: 'blob' }),
                );
            })
            .then((finalZipBlob) => {
                if (this.isExportCancelled || !finalZipBlob) return;

                this.finalZipBlob = finalZipBlob;
                this.loadingExport = false;

                if (this.progressNumber >= 100) {
                    this.onExport(this.finalZipBlob);
                }
            })
            .catch((error) => {
                this.loadingExport = false;
                this.isProgress = false;
                this.progressNumber = 0;
                clearInterval(this.interval);
            });
    }

    progressExport() {
        this.ngZone.runOutsideAngular(() => {
            this.interval = setInterval(() => {
                this.ngZone.run(() => {
                    if (this.loadingExport) {
                        this.progressNumber = Math.min(
                            this.progressNumber + Math.floor(Math.random() * 10) + 8,
                            99,
                        );
                    } else {
                        this.progressNumber = Math.min(
                            this.progressNumber + Math.floor(Math.random() * 10) + 8,
                            100,
                        );
                        if (this.progressNumber >= 100) {
                            if (this.finalZipBlob) {
                                this.onExport(this.finalZipBlob);
                            }
                        }
                    }
                });
            }, 400);
        });
    }

    onExport(finalZipBlob: Blob) {
        let nameFile = '';
        if (this.itemExportSelected?.length > 1) {
            this.itemExportSelected?.forEach((item) => {
                const exportOption = this.exportOption.find((el) => el?.key === item);
                if (exportOption) {
                    nameFile += this.translate.instant('export.' + exportOption.label) + '_';
                }
            });
        } else if (this.itemExportSelected?.length === 1) {
            const exportOption = this.exportOption.find(
                (el) => el?.key === this.itemExportSelected[0],
            );
            if (exportOption) {
                nameFile += this.translate.instant('export.' + exportOption.label);
            }
        }
        const zipUrl = URL.createObjectURL(finalZipBlob);
        const a = document.createElement('a');
        a.href = zipUrl;
        a.download = `${nameFile}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(zipUrl);
        this.isProgress = false;
        this.progressNumber = 0;
        clearInterval(this.interval);
    }

    private handleTemplateExport(exportTypes: string): Promise<Blob> {
        return new Promise((resolve) => {
            this._commonService
                .getCategories()
                .pipe(
                    filter((el: any) => el && el.length > 0),
                    switchMap((el: any) => {
                        const conditions = this.idProductSelected.map((item) => {
                            return {
                                type: 'EQUALS',
                                value: item,
                            };
                        });
                        let filter: any[] = [
                            {
                                fieldName: 'linkToParentProduct.documentId',
                                conditions: [
                                    {
                                        type: 'EQUALS',
                                        value: this.idParentProduct,
                                    },
                                ],
                            },
                            {
                                fieldName: 'childProduct.documentId',
                                conditions: conditions,
                            },
                        ];

                        let templateTypes;
                        switch (exportTypes) {
                            case 'CATALOGUE':
                                templateTypes = 'EXPORT_PRODUCT_PRICES';
                                break;
                            case 'CATALOGUE_NO_PRICE':
                                templateTypes = 'EXPORT_PRODUCT_WITHOUT_PRICES';
                                break;
                            case 'CATALOGUE_DATA_SHEET':
                                templateTypes = 'EXPORT_PRODUCT_LOGISTICS';
                                break;
                            case 'PRICE':
                                if (
                                    this.permissionService.isPermissionFonction('visibilite-tarifs')
                                ) {
                                    templateTypes = 'EXPORT_PRODUCT_PRICES';
                                } else {
                                    templateTypes = 'EXPORT_PRODUCT_WITHOUT_PRICES';
                                }
                                break;
                            case 'LOGISTIC_DATA':
                                templateTypes = 'EXPORT_PRODUCT_LOGISTICS';
                                break;
                        }

                        const body = {
                            filters: filter,
                            fields: [
                                'id',
                                'longDescription',
                                'shortDescription',
                                'brand',
                                'packaging',
                                'state',
                                'referenceProduct',
                                'countryOfManufacture',
                                'typeOfProduct',
                                'creationDate',
                                'department',
                                'lastModifiedUser',
                                'statusProduct',
                                'newStartDate',
                                'newEndDate',
                            ],
                            locales: [
                                environment.languagesExtranet.find(
                                    (lang) => lang.code === this.translate.currentLang,
                                )?.locale || 'en_GB',
                            ],
                            sorts: {},
                            page: 1,
                            pageSize: 100,
                            exportType: templateTypes,
                            /*  hideFields: hideFields, */
                        };

                        const templateType =
                            exportTypes.includes('CATALOGUE') ||
                            exportTypes.includes('CATALOGUE_NO_PRICE') ||
                            exportTypes.includes('CATALOGUE_DATA_SHEET')
                                ? 'pdf'
                                : 'xlsx';

                        return firstValueFrom(
                            this.dynamicDocumentService.exportProductByTemplate(body, templateType),
                        );
                    }),
                )
                .subscribe({
                    next: (res) => {
                        resolve(res);
                    },
                    error: (error) => {
                        const exportOption = this.exportOption.find(
                            (el) => el?.key === exportTypes,
                        );
                        this._messageService.add({
                            severity: 'error',
                            detail: this.translate.instant('export.FILE_EXPORT_ERROR', {
                                type: this.translate.instant('export.' + exportOption?.label),
                            }),
                        });
                        resolve(null as any);
                    },
                });
        });
    }
}
